import { flow, pipe } from 'fp-ts/function';
import { array, option, record, string } from 'fp-ts';
import {
    CancellationPolicyApplicability,
    PortfolioCancellationPolicy,
    PortfolioCentralCancellationPolicy,
    PortfolioCentralRateGroup,
    PortfolioCentralRateGroupOverview,
    PortfolioCentralSettlementRule,
    PortfolioEnterpriseGroup,
    PortfolioEnterpriseWithRateCount,
    PortfolioSettlementRule,
    SettlementRuleTrigger,
    PortfolioSettlementValueType,
    CancellationPolicy,
} from '@mews-commander/data-access';
import { DurationService, toDurationLuxon } from '@mews/framework';
import { RateGroup, RateGroupOverview } from './types';

export const checkIfCentralRateGroupOverview = (
    rateGroupOverview: RateGroupOverview
): rateGroupOverview is PortfolioCentralRateGroupOverview => !Object.hasOwn(rateGroupOverview, 'isPortfolioManaged');

export const checkIfCentralRateGroup = (rateGroup?: RateGroup): rateGroup is PortfolioCentralRateGroup =>
    rateGroup != null && Object.hasOwn(rateGroup, 'serviceExternalIdentifierValue');

export const checkIfCancellationPolicyIsCentral = (
    cancellationPolicy: PortfolioCentralCancellationPolicy | CancellationPolicy
): cancellationPolicy is PortfolioCentralCancellationPolicy => !Object.hasOwn(cancellationPolicy, 'applicabilityType');

export const breakDownOffset = flow(DurationService.parse, toDurationLuxon, ({ days, hours, minutes }) => ({
    days,
    hours,
    minutes,
}));

export const getAssignedPropertyGroupsProperties = (
    assignedEnterpriseGroupIds: Array<string> | null | undefined,
    propertyGroupList?: Array<PortfolioEnterpriseGroup> | null
) =>
    pipe(
        assignedEnterpriseGroupIds,
        option.fromNullable,
        option.map(
            array.filterMap((id) =>
                pipe(
                    propertyGroupList,
                    option.fromNullable,
                    option.chain(array.findFirst((propertyGroup) => propertyGroup.id === id)),
                    option.map(({ id: propertyGroupId, propertyIds }) => ({ propertyGroupId, propertyIds }))
                )
            )
        ),
        option.chain(option.fromPredicate(array.isNonEmpty))
    );

export const getRatesCountPerPropertyGroup = ({
    assignedEnterpriseGroupIds,
    centralRateGroupId,
    propertyGroups,
    properties,
}: {
    assignedEnterpriseGroupIds?: Array<string> | null;
    centralRateGroupId: string;
    propertyGroups?: Array<PortfolioEnterpriseGroup> | null;
    properties?: Array<PortfolioEnterpriseWithRateCount> | null;
}): Record<string, number> | null =>
    pipe(
        getAssignedPropertyGroupsProperties(assignedEnterpriseGroupIds, propertyGroups),
        option.map(
            array.map(({ propertyIds, propertyGroupId }) =>
                pipe(
                    propertyIds,
                    array.map((propertyId) =>
                        pipe(
                            properties,
                            option.fromNullable,
                            option.chain(array.findFirst((property) => property.id === propertyId)),
                            option.map((property) => property.rateCounts),
                            option.chain(
                                array.findFirst((rateCount) => rateCount.centralRateGroupId === centralRateGroupId)
                            ),
                            option.map(({ rateCount }) => rateCount),
                            option.getOrElse(() => 0)
                        )
                    ),
                    array.reduce(0, (acc, rateCount) => acc + rateCount),
                    (rateCount) => ({ [propertyGroupId]: rateCount })
                )
            )
        ),
        option.map(array.reduce({}, (acc, propertyGroup) => ({ ...acc, ...propertyGroup }))),
        option.getOrElseW(() => null)
    );

export const getPropertyGroupsPropertiesCount = (
    assignedEnterpriseGroupIds: Array<string> | null | undefined,
    propertyGroups?: Array<PortfolioEnterpriseGroup> | null
) =>
    pipe(
        getAssignedPropertyGroupsProperties(assignedEnterpriseGroupIds, propertyGroups),
        option.map(array.map(({ propertyIds }) => propertyIds)),
        option.map(array.flatten),
        option.map(array.uniq(string.Eq)),
        option.map(array.size),
        option.getOrElse(() => 0)
    );

type RulesByTrigger = Record<SettlementRuleTrigger, ValuesByDiscriminator>;
type ValuesByDiscriminator = Record<PortfolioSettlementValueType, number[]>;

/* Sort settlement rules by trigger so that percentage values come first, followed by flat values */
export const getSettlementRulesNames = (
    settlementRules: (PortfolioSettlementRule | PortfolioCentralSettlementRule)[],
    localizePercentageValue: (value: number) => string,
    localizeCurrencyValue: (value: number) => string,
    localizeEnum: (value: SettlementRuleTrigger) => string
) =>
    pipe(
        settlementRules,
        array.reduce({} as RulesByTrigger, (acc: RulesByTrigger, rule) => {
            if (!acc[rule.trigger]) {
                // create grouping by trigger
                acc[rule.trigger] = {} as ValuesByDiscriminator;
            }

            if (!acc[rule.trigger][rule.value.discriminator]) {
                // create grouping by value-discriminator within the trigger-grouping
                acc[rule.trigger][rule.value.discriminator] = [];
            }

            acc[rule.trigger][rule.value.discriminator].push(rule.value.value.value);

            return acc;
        }),
        record.mapWithIndex((trigger, rulesPerValueDiscriminator) =>
            pipe(
                rulesPerValueDiscriminator,
                // sort each discriminator array by value
                record.map((values) => values.toSorted((a, b) => (a - b > 0 ? 1 : -1))),
                // localize each value
                record.mapWithIndex((discriminator, values) =>
                    values.map((value) =>
                        discriminator === PortfolioSettlementValueType.Percentage
                            ? `${localizePercentageValue(value)}`
                            : `${localizeCurrencyValue(value)}`
                    )
                ),
                // append trigger to each value
                record.map((values) => values.map((value) => `${value} ${localizeEnum(trigger)}`))
            )
        ),
        // this ensures that percentages come first, followed by flat values
        record.map((triggerGrouping) => [...(triggerGrouping.Percentage || []), ...(triggerGrouping.Flat || [])]),
        // this ensures that rules with 'confirmation' trigger come first, followed by 'start', 'start date', 'end', and finally 'end date'
        (valuesPerTrigger) => [
            ...[valuesPerTrigger.Confirmation || []],
            ...[valuesPerTrigger.Start || []],
            ...[valuesPerTrigger.StartDate || []],
            ...[valuesPerTrigger.End || []],
            ...[valuesPerTrigger.EndDate || []],
        ],
        array.flatten
    );

type PoliciesByApplicability = Record<CancellationPolicyApplicability, ValuesByFeeType>;
type ValuesByFeeType = Record<'absoluteFee' | 'relativeFee', number[]>;

/* Sort cancellation policies by applicability so that relative values come first, followed by absolute values */
export const getCancellationPoliciesNames = (
    cancellationPolicies: (PortfolioCentralCancellationPolicy | PortfolioCancellationPolicy)[],
    localizePercentageValue: (value: number) => string,
    localizeCurrencyValue: (value: number) => string,
    localizeEnum: (value: CancellationPolicyApplicability) => string
) =>
    pipe(
        cancellationPolicies,
        array.reduce({} as PoliciesByApplicability, (acc: PoliciesByApplicability, policy) => {
            const { absoluteFee, relativeFee } = policy;

            if (!acc[policy.applicability]) {
                // create grouping by applicability
                acc[policy.applicability] = {} as ValuesByFeeType;
            }

            if (!acc[policy.applicability].absoluteFee) {
                // create grouping by value-discriminator within the applicability-grouping
                acc[policy.applicability].absoluteFee = [];
            }

            if (absoluteFee && absoluteFee > 0) {
                acc[policy.applicability].absoluteFee.push(absoluteFee);
            }

            if (!acc[policy.applicability].relativeFee) {
                // create grouping by value-discriminator within the applicability-grouping
                acc[policy.applicability].relativeFee = [];
            }

            if (relativeFee > 0) {
                acc[policy.applicability].relativeFee.push(relativeFee);
            }

            return acc;
        }),
        record.mapWithIndex((applicability, policiesPerType) =>
            pipe(
                policiesPerType,
                // sort each discriminator array by value
                record.map((values) => values.toSorted((a, b) => (a - b > 0 ? 1 : -1))),
                // localize each value
                record.mapWithIndex((discriminator, values) =>
                    values.map((value) =>
                        discriminator === 'relativeFee'
                            ? `${localizePercentageValue(value)}`
                            : `${localizeCurrencyValue(value)}`
                    )
                ),
                // append applicability to each value
                record.map((values) => values.map((value) => `${value} ${localizeEnum(applicability)}`))
            )
        ),
        // this ensures that relative values come first, followed by absolute values
        record.map((applicabilityGrouping) => [
            ...(applicabilityGrouping.relativeFee || []),
            ...(applicabilityGrouping.absoluteFee || []),
        ]),
        // this ensures that rules with 'Creation' applicability come first, followed by 'Start', and finally 'Start date'
        (valuesPerApplicability) => [
            ...[valuesPerApplicability.Creation || []],
            ...[valuesPerApplicability.Start || []],
            ...[valuesPerApplicability.StartDate || []],
        ],
        array.flatten
    );
